import * as TYPES from 'constants/ActionTypes';
import mime from 'mime-types';
import FileSaver from 'file-saver';
import {
  find,
  map,
  max,
  groupBy,
  sortedUniq,
  orderBy,
  get,
  uniq,
} from 'lodash';
import { reset } from 'redux-form';
import { listWM, list, create, remove, custom } from 'utils/Api';

export const requestFiles = () => ({
  type: TYPES.REQUEST_FILES,
});

export const requestFilesSuccess = (files, groupedFiles) => ({
  type: TYPES.REQUEST_FILES_SUCCESS,
  groupedFiles,
  files,
});

export const requestFilesFailure = error => ({
  type: TYPES.REQUEST_FILES_FAILURE,
  error,
});

export const fetchUserFiles = () =>
  async (dispatch, getState) => {
    dispatch(requestFiles());
    const {
      loginReducer: { user: { EmployeeMasterID } },
    } = getState();
    try {
      const getFiles = {
        url: `/kjarni/api/v2/EmployeeMasters/EmployeeDocuments(employeeMasterId=${EmployeeMasterID})`,
        method: 'GET',
      };
      const files = orderBy(get(await custom(getFiles), 'data.value', []), ['AddDate'], ['desc']);
      const groupedFiles = groupBy(files, 'ObjectDocumentType');
      dispatch(requestFilesSuccess(files, groupedFiles));
    } catch (error) {
      dispatch(requestFilesFailure(error));
    }
  };

export const setGroupFilter = groupFilter => ({
  type: TYPES.SET_GROUP_FILTER,
  groupFilter,
});

export const setPayYear = (payYear, payYearOptions) => ({
  type: TYPES.SET_PAY_YEAR,
  payYear: payYear || new Date().getFullYear(),
  payYearOptions,
});

export const requestPayslips = () => ({
  type: TYPES.REQUEST_PAYSLIPS,
});

export const requestPayslipsSuccess = payslips => ({
  type: TYPES.REQUEST_PAYSLIPS_SUCCESS,
  payslips,
});

export const requestPayslipsFailure = error => ({
  type: TYPES.REQUEST_PAYSLIPS_FAILURE,
  error,
});

export const fetchUserPayslips = () =>
  (dispatch, getState) => {
    dispatch(requestPayslips());
    const {
      loginReducer: { user: { token, EmployeeMasterID } },
      FileReducer: { payYear },
    } = getState();
    const config = {
      entity: 'PaySlip',
      headers: { Authorization: `Bearer ${token}` },
      params: {
        $filter: `Entity/EmployeeMasterID eq ${EmployeeMasterID} and (Entity/PayPeriodBegda ge datetime'${payYear}-01-01T00:00') and (Entity/PayPeriodBegda le datetime'${payYear}-12-31T00:00')`,
      },
    };

    listWM(config)
      .then(({ data }) => dispatch(requestPayslipsSuccess(data)))
      .catch(error => dispatch(requestPayslipsFailure(error)));
  };

export const initPayYear = () =>
  (dispatch, getState) => {
    const {
      loginReducer: { user: { token, EmployeeMasterID } },
    } = getState();
    const config = {
      entity: 'PaySlip',
      headers: { Authorization: `Bearer ${token}` },
      params: {
        $filter: `Entity/EmployeeMasterID eq ${EmployeeMasterID}`,
        $expand: 'Entity',
        $select: 'Entity/PayPeriodBegda',
      },
    };

    listWM(config)
      .then((payslips) => {
        const options = map(payslips.data, ({ PayPeriodBegda }) => new Date(PayPeriodBegda).getFullYear());
        const payYear = max(options);
        const payYearOptions = sortedUniq(options);
        dispatch(setPayYear(payYear, uniq(payYearOptions)));
        dispatch(fetchUserPayslips());
      })
      .catch(error => dispatch(requestPayslipsFailure(error)));
  };

export const requestFileDownload = () => ({
  type: TYPES.REQUEST_FILE_DOWNLOAD,
});

export const requestFileDownloadSuccess = () => ({
  type: TYPES.REQUEST_FILE_DOWNLOAD_SUCCESS,
});

export const requestFileDownloadFailure = error => ({
  type: TYPES.REQUEST_FILE_DOWNLOAD_FAILURE,
  error,
});

const dataURItoBlob = (byteString, type) => {
  // write the bytes of the string to a typed array
  const ia = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i += 1) {
    ia[i] = byteString.charCodeAt(i);
  }

  return new Blob([ia], { type });
};

export const download = (fileObject) => {
  const docName = `${fileObject.Name}${fileObject.Extension}`;
  const type = mime.lookup(docName);
  FileSaver.saveAs(dataURItoBlob(atob(fileObject.FileSource), type), docName);
};

export const downloadFile = documentId =>
  async (dispatch) => {
    try {
      dispatch(requestFileDownload());
      const getFile = {
        url: `/kjarni/api/v2/FileRepositories/${documentId}/GetDocument`,
        method: 'GET',
      };
      const data = get(await custom(getFile), 'data');

      download(data);

      dispatch(requestFileDownloadSuccess());
    } catch (error) {
      dispatch(requestFileDownloadFailure(error));
    }
  };

export const requestFileDelete = () => ({
  type: TYPES.REQUEST_FILE_DELETE,
});

export const requestFileDeleteSuccess = () => ({
  type: TYPES.REQUEST_FILE_DELETE_SUCCESS,
});

export const requestFileDeleteFailure = error => ({
  type: TYPES.REQUEST_FILE_DELETE_FAILURE,
  error,
});

export const deleteFile = removeDoc =>
  (dispatch, getState) => {
    dispatch(requestFileDelete());
    const { loginReducer: { user: { token } } } = getState();
    const config = {
      entity: 'FileRepository',
      headers: { Authorization: `Bearer ${token}` },
      data: removeDoc,
    };

    remove(config)
      .then(() => {
        dispatch(requestFileDeleteSuccess());
        dispatch(fetchUserFiles());
      })
      .catch(error => dispatch(requestFileDeleteFailure(error)));
  };

export const requestFileUpload = () => ({
  type: TYPES.REQUEST_FILE_UPLOAD,
});

export const requestFileUploadSuccess = () => ({
  type: TYPES.REQUEST_FILE_UPLOAD_SUCCESS,
});

export const requestFileUploadFailure = error => ({
  type: TYPES.REQUEST_FILE_UPLOAD_FAILURE,
  error,
});

export const uploadFile = ({ file, fileType }, idCallBack) =>
  (dispatch, getState) => {
    dispatch(requestFileUpload());
    const { FileReducer: { ownerTypes }, loginReducer: { user: { token, EmployeeMasterID } } } = getState();
    // Encode file data
    const ownerType = find(ownerTypes, ['Value', 'EmployeeMaster']);
    // If we cannot find the correct entity owner type we throw an error
    if (ownerType === undefined || !ownerType.FileRepositoryEntityOwnerTypeID) {
      dispatch(requestFileUploadFailure(new Error('Ekki er hægt að vista skjal á EmployeeMaster')));
    } else if (file === undefined || !file.name) {
      dispatch(requestFileUploadFailure(new Error('Ekki neitt skjal var valið')));
    } else {
      const reader = new FileReader();
      const AddDate = new Date().toISOString();
      reader.onload = () => {
        const FileSource = reader.result.substring(reader.result.indexOf(',') + 1);
        const Name = file.name.substring(0, file.name.indexOf('.'));
        const Extension = file.name.substring(file.name.indexOf('.'));
        const config = {
          entity: 'FileRepository',
          headers: { Authorization: `Bearer ${token}` },
          data: {
            EntityType: ownerType.FileRepositoryEntityOwnerTypeID,
            Entity_ID: EmployeeMasterID,
            FileSource,
            ObjectDocumentType: fileType,
            Name,
            Extension,
            AddDate,
          },
        };

        return create(config)
          .then((createResult) => {
            dispatch(requestFileUploadSuccess());
            if (idCallBack) {
              idCallBack(createResult.data);
            } else {
              dispatch(reset('fileUpload'));
              dispatch(fetchUserFiles());
            }
          })
          .catch(error => dispatch(requestFileUploadFailure(error)));
      };
      reader.onerror = e => dispatch(requestFileUploadFailure(e));
      reader.readAsDataURL(file);
    }
  };

export const uploadFiles = ({ files, fileType }, idCallBack) =>
  (dispatch) => {
    Array.from(files).forEach((file) => {
      dispatch(uploadFile({ file, fileType }, () => {
        idCallBack();
      }));
    });
  };

export const initFileUpload = () => ({
  type: TYPES.INIT_FILE_UPLOAD,
});

export const initFileUploadSuccess = ({ ownerTypes, documentTypes }) => ({
  type: TYPES.INIT_FILE_UPLOAD_SUCCESS,
  ownerTypes,
  documentTypes,
});

export const initFileUploadFailure = error => ({
  type: TYPES.INIT_FILE_UPLOAD_FAILURE,
  error,
});

export const initUpload = () =>
  (dispatch, getState) => {
    dispatch(initFileUpload());
    const { loginReducer: { user: { token } } } = getState();
    const ownerTypeConfig = {
      entity: 'FileRepositoryEntityOwnerType',
      headers: { Authorization: `Bearer ${token}` },
    };
    const documentTypeConfig = {
      entity: 'FileRepositoryObjectDocumentType',
      headers: { Authorization: `Bearer ${token}` },
    };

    list(ownerTypeConfig)
      .then(oT =>
        list(documentTypeConfig)
          .then((dT) => {
            dispatch(initFileUploadSuccess({
              ownerTypes: oT.data, documentTypes: orderBy(dT.data, 'Name'),
            }));
            dispatch(fetchUserFiles());
          })
          .catch(error => dispatch(initFileUploadFailure(error))))
      .catch(error => dispatch(initFileUploadFailure(error)));
  };

export const requestFileById = () => ({
  type: TYPES.REQUEST_FILE_BY_ID,
  fileById: {},
  fileLoaded: false,
  sentAt: Date.now(),
  error: null,
});

export const requestFileByIdSuccess = fileById => ({
  type: TYPES.REQUEST_FILE_BY_ID_SUCCESS,
  fileById,
  loaded: true,
  receivedAt: Date.now(),
  error: null,
});

export const requestFileByIdFailure = error => ({
  type: TYPES.REQUEST_FILE_BY_ID_FAILURE,
  fileById: {},
  loaded: true,
  receivedAt: Date.now(),
  error,
});

export const getFileById = documentId =>
  async (dispatch) => {
    try {
      dispatch(requestFileById());
      const getFile = {
        url: `/kjarni/api/v2/FileRepositories/${documentId}/GetDocument`,
        method: 'GET',
      };
      const data = get(await custom(getFile), 'data');

      dispatch(requestFileByIdSuccess(data));
    } catch (error) {
      dispatch(requestFileByIdFailure(error));
    }
  };



// WEBPACK FOOTER //
// ./src/actions/Files.js